import { useMemo, useRef, useState } from 'react'
import Modal from 'core/components/dialogs/Modal'
import Button from 'core/components/buttons/Button'
import { useBlocker } from 'react-router'

export interface UnsavedModalProps {
  overrideShow?: boolean
  active: boolean
  onSave?: (location: string) => void
  onClose?: VoidFunction
  onExit?: (location: string, search?: string) => void
  storybookOverride?: boolean
  discardButtonText?: string
  saveButtonText?: string
}

const UnsavedModal = ({
  overrideShow = false,
  active = true,
  onExit,
  onSave,
  onClose,
  storybookOverride,
  discardButtonText = 'Discard changes',
  saveButtonText = 'Save & exit'
}: UnsavedModalProps) => {
  const [location, setLocation] = useState('')
  const [search, setSearch] = useState('')
  const isExiting = useRef(false)

  const { state, reset } = useBlocker(({ currentLocation, nextLocation }) => {
    if (isExiting.current) {
      isExiting.current = false
      return false
    }
    setLocation(nextLocation.pathname)
    setSearch(nextLocation.search)
    return active && currentLocation.key !== nextLocation.key
  })

  const footerButtons = useMemo(() => {
    return (
      <div className="flex flex-row gap-4">
        <Button
          variant="outline"
          onClick={() => {
            isExiting.current = true
            reset?.()
            onExit?.(location, search)
          }}
          key="discard"
        >
          {discardButtonText}
        </Button>
        <Button
          onClick={() => {
            reset?.()
            onSave?.(location)
          }}
          variant="primary"
          key="save"
        >
          {saveButtonText}
        </Button>
      </div>
    )
  }, [
    discardButtonText,
    saveButtonText,
    reset,
    onExit,
    location,
    search,
    onSave
  ])

  return (
    <Modal
      closeButton={<span />}
      title="You have unsaved changes"
      footer={footerButtons}
      backdropClassName="z-[50]!"
      show={
        state === 'blocked' || (storybookOverride && active) || overrideShow
      }
      onClose={() => {
        onClose?.()
        reset?.()
      }}
    >
      <p>
        Are you sure you want to leave this screen and discard your changes?
      </p>
    </Modal>
  )
}

export default UnsavedModal
